import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { TextField, Box, Typography, SxProps, Theme } from '@mui/material';
import { useTranslation } from 'react-i18next';

const sxStyles: Record<string, SxProps<Theme>> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    padding: 2,
  },
  codeInput: {
    '& input': {
      fontSize: 'h5.fontSize',
      letterSpacing: '0.5rem',
      textAlign: 'center',
      fontFamily: 'monospace',
    },
  },
  description: {
    color: 'text.secondary',
    fontSize: 'body2.fontSize',
  },
  errorText: {
    color: 'error.main',
    fontSize: 'body2.fontSize',
    marginTop: 1,
  },
};

export type VerificationCodeFormHandle = {
  getCode: () => string;
  setError: (error: string | null) => void;
};

type VerificationCodeFormProps = {
  error?: string | null;
};

const VerificationCodeForm = forwardRef<VerificationCodeFormHandle, VerificationCodeFormProps>(
  (_, ref) => {
    const [code, setCode] = useState('');

    const [error, setError] = useState<string | null>(null);
    const { t } = useTranslation();

    const handleCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value.replace(/\D/g, '').slice(0, 6);
      setCode(value);
    };

    useImperativeHandle(
      ref,
      () => ({
        getCode: () => code,
        setError,
      }),
      [code]
    );

    return (
      <Box sx={sxStyles.container}>
        <Typography variant="body1">{t('smartmessaging.diffusion2FA.enterCodePrompt')}</Typography>
        <Typography sx={sxStyles.description}>
          {t('smartmessaging.diffusion2FA.codeInfo')}
        </Typography>
        <TextField
          autoFocus
          fullWidth
          variant="outlined"
          placeholder="000000"
          value={code}
          onChange={handleCodeChange}
          slotProps={{
            htmlInput: {
              maxLength: 6,
              pattern: '[0-9]*',
              inputMode: 'numeric',
            },
          }}
          sx={sxStyles.codeInput}
          error={!!error}
        />
        {error && <Typography sx={sxStyles.errorText}>{error}</Typography>}
      </Box>
    );
  }
);

export default VerificationCodeForm;
